import { formatPhone, normalizePhone } from '@fundline/shared/phone';
import {
  SIMULATED_AUTORESPONSES,
  SimulateInboundResponseSchema,
} from '@fundline/shared/schemas/dev';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { workerApi } from '../../lib/worker-api.ts';
import { useNumbersQuery } from '../settings/use-numbers.ts';

const QUICK = ['STOP', 'START', 'HELP', 'Stop by the office tomorrow', 'Yes, send the application'];

/**
 * The dev simulator (local only: the route exists when VITE_APP_ENV=local, and the worker answers only
 * with DEV_SIMULATOR_ENABLED=true). Sends a fake inbound text from any number to one of the
 * workspace's fake numbers, through the real webhook path and the opt-out gate.
 */
export function SimulatorPage() {
  const { workspaceId } = useWorkspaceClaims();
  const numbers = useNumbersQuery(workspaceId);
  const fake = (numbers.data ?? []).filter((n) => n.provider === 'fake' && n.status !== 'released');
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('');
  const [text, setText] = useState('');
  const [auto, setAuto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const toNumber = to || fake[0]?.e164 || '';

  const send = useMutation({
    mutationFn: (body: { to: string; from: string; text: string; autoresponseType?: string }) =>
      workerApi(`/workspaces/${workspaceId}/dev/inbound`, {
        method: 'POST',
        body,
        schema: SimulateInboundResponseSchema,
      }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const phone = normalizePhone(from);
    if (!phone.ok) {
      setError(phone.reason);
      return;
    }
    setError(null);
    send.mutate({
      to: toNumber,
      from: phone.e164,
      text,
      ...(auto ? { autoresponseType: auto } : {}),
    });
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Simulator"
        description="Local development only. Sends a fake text from a merchant through the real webhook path."
      />
      {numbers.isPending ? <Spinner /> : null}
      {numbers.isError ? <Alert tone="error">{numbers.error.message}</Alert> : null}
      {numbers.data && fake.length === 0 ? (
        <EmptyState title="No fake number yet">
          Add one in{' '}
          <Link to="/settings/numbers" className="underline">
            Settings → Numbers
          </Link>
          .
        </EmptyState>
      ) : null}
      {fake.length > 0 ? (
        <Card title="Text from a merchant">
          <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <SelectField
              label="To (our number)"
              value={toNumber}
              onChange={(e) => setTo(e.target.value)}
              options={fake.map((n) => ({ value: n.e164, label: formatPhone(n.e164) }))}
            />
            <TextField
              label="From (merchant phone, known or new)"
              inputMode="tel"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              error={error}
              placeholder="(212) 555-0177"
            />
            <TextAreaField
              label="Text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={1600}
            />
            <div className="flex flex-wrap gap-2" role="group" aria-label="Quick texts">
              {QUICK.map((q) => (
                <Button key={q} variant="secondary" size="sm" onClick={() => setText(q)}>
                  {q}
                </Button>
              ))}
            </div>
            <SelectField
              label="Pretend the carrier's keyword handling answered"
              value={auto}
              onChange={(e) => setAuto(e.target.value)}
              options={[
                { value: '', label: 'No (we answer)' },
                ...SIMULATED_AUTORESPONSES.map((a) => ({ value: a, label: `Yes, ${a}` })),
              ]}
            />
            {send.isError ? <Alert tone="error">{send.error.message}</Alert> : null}
            {send.isSuccess ? (
              <Alert tone="success">
                Sent ({send.data.eventId}).{' '}
                <Link to="/inbox" className="underline">
                  Open the inbox
                </Link>
                .
              </Alert>
            ) : null}
            <div>
              <Button type="submit" busy={send.isPending}>
                Send fake text
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
